import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '../components';
import { Users, Target, Shield, TrendingUp, CheckCircle, ArrowRight } from 'lucide-react';

export const ProfessionalWelcomePage: React.FC = () => {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();

  const features = [
    {
      icon: <Target className="h-6 w-6" />,
      title: t('welcome.feature1Title'),
      description: t('welcome.feature1Desc')
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: t('welcome.feature2Title'),
      description: t('welcome.feature2Desc')
    },
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: t('welcome.feature3Title'),
      description: t('welcome.feature3Desc')
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: t('welcome.feature4Title'),
      description: t('welcome.feature4Desc')
    }
  ];

  const professionalTypes = [
    t('welcome.type1'),
    t('welcome.type2'),
    t('welcome.type3')
  ];

  const handleStartOnboarding = () => {
    navigate('/professional/onboarding');
  };

  return (
    <div className="min-h-screen bg-gradient-from-blue-50 to-indigo-100">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              {t('welcome.heroTitle')} <span className="text-blue-600">BeLagom</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              {t('welcome.heroSubtitle')}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12">
        {/* Who We Are */}
        <Card className="mb-12">
          <div className="p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('welcome.whoAreWe')}</h2>
              <p className="text-lg text-gray-600">
                {t('welcome.whoAreWeDesc')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('welcome.ourMission')}</h3>
                <p className="text-gray-600 mb-4">
                  {t('welcome.ourMissionText1')}
                </p>
                <p className="text-gray-600">
                  {t('welcome.ourMissionText2')}
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('welcome.whatCanWeDo')}</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-600">{t('welcome.benefit1')}</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-600">{t('welcome.benefit2')}</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-600">{t('welcome.benefit3')}</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-600">{t('welcome.benefit4')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </Card>

        {/* Features */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
            {t('welcome.howWeWork')}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <Card key={index}>
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                      {feature.icon}
                    </div>
                    <h3 className="ml-4 text-lg font-semibold text-gray-900">
                      {feature.title}
                    </h3>
                  </div>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Professional Types */}
        <Card className="mb-12">
          <div className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
              {t('welcome.professionalsWeWorkWith')}
            </h2>
            <div className="text-center">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('welcome.initially')}</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {professionalTypes.map((type, index) => (
                    <div key={index} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <span className="text-blue-900 font-medium">{type}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-6 border-t border-gray-200">
                <h3 className="text-lg font-medium text-gray-700 mb-2">{t('welcome.nearFuture')}</h3>
                <p className="text-gray-500">{t('welcome.nearFutureTypes')}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* CTA */}
        <div className="text-center pb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {t('welcome.readyToCollaborate')}
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            {t('welcome.startScreeningDesc')}
          </p>

          <Button
            onClick={handleStartOnboarding}
            size="lg"
            className="text-lg px-8 py-4"
          >
            {t('welcome.startScreening')}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>

          <p className="mt-4 text-sm text-gray-500">
            {t('welcome.screeningDuration')}
          </p>
        </div>
      </div>
    </div>
  );
};
